import { Card, Box, IconButton, Tooltip, BoxProps, Typography, Button, CircularProgress } from "@mui/material"
import { ListContextProvider, ReduxState, useListContext, useRefresh } from "ra-core"
import { FC, useMemo } from "react"
import { Content } from "~/components/content/Content"
import { FunctionField, TextField } from "~/components/field"
import { Datagrid } from "~/components/list/datagrid"
import Scrollbar from "~/components/Scrollbar"
import { OdResourcePageProps } from "~/types/resource"
import DefaultPagination from '~/components/list/pagination/Pagination'
import { Link as RouterLink } from 'react-router-dom'
import SizeTextField from "~/components/field/SizeTextField"
import DateField2 from "~/components/field/DateField2"
import { RecPatch } from "~/dataProvider/tools"
import ArrowRightIcon from '~/icons/ArrowRight';
import DeployDatabase from "~/components/deployment/DeployDatabase"
import { genUrlWithHashid } from "~/utils/hashid"
import { DeploymentResponseDto, DeploymentStatus, InstanceStatus } from "~/gen/api/deploy"
import { useResourceRegister } from "~/utils/resourceRegister"
import { StatusIndicator } from "~/components/deployment/DeploymentInfo"
import LogViewer, { useLogViewerController, LogViewerProvider } from "~/components/backups/LogViewer"
import useLocalListController from "~/components/list/useLocalListController"
import NavigationRefresh from '@mui/icons-material/Refresh';
import { useSelector } from "react-redux"
import { AtopFuncArea, FilterField, FilterOption, SearchField } from "~/components/list/datagridFunctionWidgets"
import DatabaseNameField from "~/components/databases/DatabaseNameField"
import { EncryptedDatabasesContextProvider } from "~/components/databases/useEncryptedDatabases"
import { useHasPermit } from "~/authProvider/useHasPermit"
import { MANAGE_DATABASE_DEPLOYMENTS } from "~/constants"



const databaseListOptions : {sorters: Record<string, (a:DeploymentResponseDto & RecPatch, b:DeploymentResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    databaseName: (a, b) => (a.name || '') < (b.name || '') ? -1 : 1,
    sizeText: (a, b) => (a.size || 0) - (b.size || 0),
    deployedTime: (a, b) => (a.deployedTime || '') < (b.deployedTime || '') ? -1 : 1,
  }
}

const DeploymentList: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: 'encryptable-backups' })
  useResourceRegister({ name: 'encryption-historic-recs' })
  useResourceRegister({ name: 'host-options' })
  // useResourceRegister({ name: 'settings/organization/cloud-subscription' })

  const listCtrls = useLocalListController({ resource: 'deployments/list', basePath: '' }, databaseListOptions)
  return (
    <ListContextProvider value={listCtrls} >
      <DeploymentListBody {...props} />
    </ListContextProvider>
  )
}

const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Stopped', value: 'Stopped' },
  { label: 'Offline', value: 'Offline' },
  { label: 'Warning', value: 'Warning' },
  { label: 'Failed', value: 'Failed' },
]

const map = {
  'Active': (d: DeploymentResponseDto) => d.status === InstanceStatus.Online,
  'In Progress': (d: DeploymentResponseDto) => [DeploymentStatus.InProgress, DeploymentStatus.Deleting, DeploymentStatus.Restarting, DeploymentStatus.Stopping].some(x => x === d.deploymentStatus),
  'Stopped': (d: DeploymentResponseDto) => d.deploymentStatus === DeploymentStatus.Stopped,
  'Offline': (d: DeploymentResponseDto) => d.status !== InstanceStatus.Online && d.deploymentStatus === DeploymentStatus.Success,
  'Warning': (d: DeploymentResponseDto) => d.deploymentStatus === DeploymentStatus.Warning,
  'Failed': (d: DeploymentResponseDto) => d.deploymentStatus === DeploymentStatus.Failed,
}

const statusMatch = (d: DeploymentResponseDto, fval:FilterOption) => {
  const fn = (map as any)[fval.value]
  return !!fn && fn(d)
}

export const DeploymentListBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_DEPLOYMENTS])
  const listCtx = useListContext()

  const logCtrl = useLogViewerController()
  const { showLogInViewer } = logCtrl
  
  const loading = useSelector<ReduxState>(state => state.admin.loading > 0);
  const refresh = useRefresh()

  
  const data = useSelector<ReduxState, Record<number, DeploymentResponseDto & RecPatch>>(store => store.admin.resources['deployments/list'].data)
  const deploymentsAll = Object.values(data || {})

  const engineOptions = useMemo(() => Object.values(
    deploymentsAll.reduce((acc, d) => {
      if (d.engineId) {
        const option = ({
          label: `${d.engineVersion || ''}${d.engineEdition ? ' ' + d.engineEdition : ''}`,
          value: d.engineId
        })
        acc[d.engineId] = option
      }
      return acc
    }, {} as Record<number, {label:string,value:number}>)
  ).sort((a, b) => a.label < b.label ? -1 : 1), [ JSON.stringify([...new Set(deploymentsAll.map(d => d.engineId))].sort()) ])
  

  return (
    <Content
      pageName="Deployment List"
      resourceName="Deployment List"
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        hasWritePermit &&
        <DeployDatabase />
      }
      secondaryActions={
        <Button
          size="small"
          startIcon={loading ?<CircularProgress size={14} /> : <NavigationRefresh />}
          color="secondary"
          onClick={() => refresh()}
        >
          Refresh
        </Button>
      }
    >
      <LogViewerProvider value={logCtrl}>
        <EncryptedDatabasesContextProvider>
          <Card sx={{ mt: 2 }}>
            <AtopFuncArea>
              <SearchField 
                sourceKey="dbName"
                match={(d: DeploymentResponseDto, fval:string) => !!d.name && d.name.toLowerCase().includes(fval.trim().toLowerCase())}
                sx={{ width: 280 }}
                TextFieldProps={{ placeholder: 'Search by Database Name' }}
              />
              <FilterField 
                label="Engine" 
                filterOptions={engineOptions} 
                sourceKey="engine"
                match={(d: DeploymentResponseDto, fval:FilterOption) => d.engineId === fval.value}
                sx={{ width: 320 }}
              />
              <FilterField 
                label="Status" 
                filterOptions={statusOptions} 
                sourceKey="status"
                match={statusMatch}
                sx={{ width: 160 }}
              />
            </AtopFuncArea>
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false}
                  sx={{
                    '& th': { py: 2 },
                    '& td': { py: 2 },
                  }}
                  {...listCtx}
                >
                  <DatabaseNameField label="Database Name" />
                  <TextField source="hostingSpecification" label="Hosting Specification" sortable={false} />
                  <FunctionField 
                    source="engineVersion" 
                    label="Engine Version" 
                    sortable={false} 
                    render={(d?: DeploymentResponseDto & RecPatch) => (
                      <Typography
                        component="p"
                        variant="body2"
                      >
                        {d?.engineVersion + ' ' + (d?.engineEdition || '')}
                      </Typography>
                    )}
                  />
                  <SizeTextField source="sizeText" numericSource="size" label="Size" emptyText="N/A" />
                  <DateField2 source="deployedTime" label="Last Started" showTime />
                  <FunctionField 
                    source="status" 
                    label="Status" 
                    sortable={false} 
                    render={(d?: DeploymentResponseDto & RecPatch) => (
                      <StatusIndicator 
                        color={(
                          !d ? 'inherit'
                          : d.status === InstanceStatus.Online ? 'success.main'
                          : ([DeploymentStatus.InProgress, DeploymentStatus.Deleting, DeploymentStatus.Restarting, DeploymentStatus.Stopping]
                              .some(x => x === d.deploymentStatus)) ? 'text.disabled'
                          : d.deploymentStatus === DeploymentStatus.Warning ? 'warning.main'
                          : d.deploymentStatus === DeploymentStatus.Failed ? 'error.main'
                          : 'text.disabled') as BoxProps['color']
                        }
                        showViewLog={!!d && d.deploymentStatus === DeploymentStatus.Failed && !!d.message}
                        viewLogClick={(e:any) => showLogInViewer(e, d?.message || '')}
                        title={
                          !d ? undefined
                          : d.status === InstanceStatus.Online ? 'Active'
                          : (d.deploymentStatus !== undefined && d.deploymentStatus !== DeploymentStatus.Success
                              ? (DeploymentStatus[d.deploymentStatus] || '').replace(/[A-Z]+/g, (m:string) => { return ' ' + m}) as any
                              : 'Offline')
                        }
                      />
                    )}
                  />
                  <FunctionField
                    label="Action"
                    sortable={false}
                    render={(d?: DeploymentResponseDto & RecPatch) => (
                      <Tooltip title="Details">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          component={RouterLink}
                          to={genUrlWithHashid(`/deployments/list/$0/show`, d?.id || 0, d?.name)}
                        >
                          <ArrowRightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore 
              <DefaultPagination {...listCtx} />}
          </Card>
        </EncryptedDatabasesContextProvider>
        <LogViewer />
      </LogViewerProvider>
    </Content>
  )
}


export default DeploymentList;
